/**
 * UI State Handlers
 * Handles undo/redo for UI state operations (selection, expansion, filtering, etc.)
 */

import type { Operation, UIOperation, ViewFilter } from '@/shared/types/projectData';
import type { OperationHandler, StateSetters } from '../types';

function toStringSet(value: unknown): Set<string> {
  if (value instanceof Set) {
    return new Set([...value].filter((item): item is string => typeof item === 'string'));
  }
  if (Array.isArray(value)) {
    return new Set(value.filter((item): item is string => typeof item === 'string'));
  }
  return new Set();
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function asViewFilter(value: unknown): ViewFilter {
  return asRecord(value) as ViewFilter;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Factory function to create UI state handlers
 */
function createUIStateHandler<T = unknown>(
  beforeGetter: (op: UIOperation) => T,
  afterGetter: (op: UIOperation) => T,
  setter: (value: T) => void
): OperationHandler {
  return {
    undo: async (op: Operation) => {
      const uiOp = op as UIOperation;
      setter(beforeGetter(uiOp));
    },
    redo: async (op: Operation) => {
      const uiOp = op as UIOperation;
      setter(afterGetter(uiOp));
    }
  };
}

/**
 * Register all UI state operation handlers
 */
export function registerUIStateHandlers(
  handlers: Map<string, OperationHandler>,
  setters: StateSetters
): void {
  // Selection
  handlers.set('select-node', createUIStateHandler(
    (op) => toStringSet((op.details.before as { selected?: unknown } | undefined)?.selected),
    (op) => toStringSet((op.details.after as { selected?: unknown } | undefined)?.selected),
    (value) => setters.setSelectedNodeIds(value)
  ));

  // Expansion
  handlers.set('toggle-expand', createUIStateHandler(
    (op) => toStringSet((op.details.before as { expanded?: unknown } | undefined)?.expanded),
    (op) => toStringSet((op.details.after as { expanded?: unknown } | undefined)?.expanded),
    (value) => setters.setExpandedNodeIds(value)
  ));

  // Filtering
  handlers.set('filter-change', createUIStateHandler(
    (op) => asViewFilter(op.details.before),
    (op) => asViewFilter(op.details.after),
    (value) => setters.setFilters(value)
  ));

  // Search
  handlers.set('search-change', createUIStateHandler(
    (op) => asString(asRecord(op.details.before).searchQuery),
    (op) => asString(asRecord(op.details.after).searchQuery),
    (value) => setters.setSearchQuery(value)
  ));

  // Sort
  handlers.set('sort-change', createUIStateHandler(
    (op) => asNullableString(asRecord(op.details.before).sortBy),
    (op) => asNullableString(asRecord(op.details.after).sortBy),
    (value) => setters.setSortBy(value)
  ));

  // View
  handlers.set('view-change', createUIStateHandler(
    (op) => asString(asRecord(op.details.before).activeView),
    (op) => asString(asRecord(op.details.after).activeView),
    (value) => setters.setActiveView(value)
  ));

  // View mode
  handlers.set('view-mode-change', createUIStateHandler(
    (op) => asBoolean(asRecord(op.details.before).isCompactView),
    (op) => asBoolean(asRecord(op.details.after).isCompactView),
    (value) => setters.setIsCompactView(value)
  ));

  // Delegate UI state change to specific handler
  handlers.set('ui-state-change', {
    undo: async (op: Operation) => {
      const uiOp = op as UIOperation;
      const operationType = uiOp.details.operationType;
      if (operationType) {
        const handler = handlers.get(operationType);
        if (handler) await handler.undo(op);
      }
    },
    redo: async (op: Operation) => {
      const uiOp = op as UIOperation;
      const operationType = uiOp.details.operationType;
      if (operationType) {
        const handler = handlers.get(operationType);
        if (handler) await handler.redo(op);
      }
    }
  });
}
